import { initPostHog } from "./config/posthog";
import { initSentry, isExpectedGraphQLError, Sentry } from "./config/sentry";
import { captureError } from "./utils/logger";

initSentry();
initPostHog();

window.addEventListener("unhandledrejection", (event) => {
	captureError(event.reason, { operation: "unhandledRejection" });
});

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/design-tokens.css";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { theme } from "./theme";
import "./index.css";
import {
	ApolloClient,
	ApolloProvider,
	from,
	HttpLink,
	InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { StrictMode } from "react";
import { config } from "./config/env";
import { typePolicies } from "./graphql/typePolicies";
import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const sentryLink = onError(({ graphQLErrors, networkError, operation }) => {
	if (graphQLErrors) {
		for (const error of graphQLErrors) {
			if (isExpectedGraphQLError(error)) continue;
			const code = error.extensions?.code as string;

			const sentryError = new Error(error.message);
			sentryError.name = "GraphQLError";

			Sentry.captureException(sentryError, {
				tags: {
					graphql_operation: operation.operationName || "anonymous",
					graphql_error_code: code,
				},
				extra: {
					path: error.path,
					locations: error.locations,
					extensions: error.extensions,
				},
			});
		}
	}
	if (networkError) {
		Sentry.captureException(networkError, { tags: { type: "network_error" } });
	}
});

const httpLink = new HttpLink({
	uri: config.graphqlUrl,
	credentials: "include",
});

const client = new ApolloClient({
	link: from([sentryLink, httpLink]),
	cache: new InMemoryCache({ typePolicies }),
});

const router = createRouter({ routeTree });

router.subscribe("onResolved", ({ toLocation }) => {
	Sentry.addBreadcrumb({
		category: "navigation",
		message: `Navigated to ${toLocation.pathname}`,
		level: "info",
	});
});

const root = document.getElementById("root");

// App component that always wraps with AuthKitProvider
function App() {
	const appContent = (
		<ApolloProvider client={client}>
			<MantineProvider
				theme={theme}
				defaultColorScheme="dark"
				forceColorScheme="dark"
			>
				<ModalsProvider>
					<Notifications />
					<RouterProvider router={router} />
				</ModalsProvider>
			</MantineProvider>
		</ApolloProvider>
	);

	// Always use AuthKitProvider, but with fallback client ID if not configured
	return (
		<AuthKitProvider
			clientId={config.workosClientId || "fallback-client-id"}
			redirectUri={`${config.apiBaseUrl}/v1/auth/callback`}
		>
			{appContent}
		</AuthKitProvider>
	);
}

if (root && !root.innerHTML) {
	createRoot(root).render(
		<StrictMode>
			<App />
		</StrictMode>,
	);
}
